import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../utils/auth';
import { API_URL } from '../config';
import { useRoleActions } from '../context/RoleContext';
import { SearchInput, Input, DropdownMenu, ActionsMenuDotsIcon } from './UI';
import s from './Dexter.module.css';

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '—';
  if (bytes === 0) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  return `${size >= 10 || unitIndex === 0 ? Math.round(size) : size.toFixed(1)} ${units[unitIndex]}`;
}

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('ru-RU');
}

function openFileUrl(url, filename, isIOS = false) {
  if (isIOS) {
    window.location.assign(url);
    return;
  }

  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noreferrer';
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function extractErrorText(text, fallback) {
  if (!text) return fallback;
  try {
    const parsed = JSON.parse(text);
    return parsed?.detail || fallback;
  } catch {
    return text || fallback;
  }
}

function pluralize(count, forms) {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return forms[2];
  if (n1 > 1 && n1 < 5) return forms[1];
  if (n1 === 1) return forms[0];
  return forms[2];
}

function getFileExt(filename) {
  if (!filename || typeof filename !== 'string') return '';
  const parts = filename.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1].toLowerCase();
}

function getFileBadgeType(filename) {
  const ext = getFileExt(filename);
  if (ext === 'pdf') return { label: 'PDF', className: 'fileTypePdf' };
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return { label: 'XLS', className: 'fileTypeXls' };
  if (ext === 'doc' || ext === 'docx' || ext === 'rtf') return { label: 'DOC', className: 'fileTypeDoc' };
  if (ext === 'ppt' || ext === 'pptx') return { label: 'PPT', className: 'fileTypePpt' };
  if (ext === 'jpg' || ext === 'jpeg' || ext === 'png' || ext === 'gif' || ext === 'webp' || ext === 'svg') return { label: 'IMG', className: 'fileTypeImg' };
  if (ext === 'zip' || ext === 'rar' || ext === '7z') return { label: 'ZIP', className: 'fileTypeZip' };
  return { label: (ext || 'FILE').slice(0, 4).toUpperCase(), className: 'fileTypeDefault' };
}

function getUploadFileKey(file) {
  if (!file) return '';
  return `${file.name}-${file.size}-${file.lastModified}`;
}

export default function Dexter() {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const currentFolderId = folderId ? Number(folderId) : null;
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  const { hasPermission } = useRoleActions();
  const canManageDexter = hasPermission('dexter.manage');
  const showActionsColumn = canManageDexter || isIOS;

  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [currentFolder, setCurrentFolder] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);

  const [folderName, setFolderName] = useState('');
  const [isCreateFolderFormOpen, setIsCreateFolderFormOpen] = useState(false);
  const [isUploadFormOpen, setIsUploadFormOpen] = useState(false);
  const [renameFolderId, setRenameFolderId] = useState(null);
  const [renameFolderName, setRenameFolderName] = useState('');
  const [openFolderMenuId, setOpenFolderMenuId] = useState(null);

  const [filesToUpload, setFilesToUpload] = useState([]);
  const [isDragActive, setIsDragActive] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const mergeFiles = useCallback((incomingFiles) => {
    const nextFiles = Array.from(incomingFiles || []);
    if (!nextFiles.length) return;

    setFilesToUpload((prev) => {
      const merged = [...prev];
      const seen = new Set(
        prev.map((file) => `${file.name}-${file.size}-${file.lastModified}`)
      );

      for (const file of nextFiles) {
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (seen.has(key)) continue;
        seen.add(key);
        merged.push(file);
      }

      return merged;
    });
  }, []);

  const isRoot = currentFolderId == null;
  const trimmedSearchQuery = searchQuery.trim();
  const isSearchMode = trimmedSearchQuery.length > 0;

  const openRoot = useCallback(() => {
    navigate('/dexter');
  }, [navigate]);

  const openFolder = useCallback(
    (id) => {
      navigate(`/dexter/folder/${id}`);
    },
    [navigate]
  );

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const url = isRoot
        ? `${API_URL}/dexter/folders/root`
        : `${API_URL}/dexter/folders/${currentFolderId}/items`;

      const res = await apiFetch(url);
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось загрузить данные'));
      }

      const data = await res.json();

      if (isRoot) {
        setCurrentFolder(null);
        setBreadcrumbs([]);
        setFolders(Array.isArray(data) ? data : []);
        setFiles([]);
      } else {
        setCurrentFolder(data?.current_folder || null);
        setBreadcrumbs(Array.isArray(data?.breadcrumbs) ? data.breadcrumbs : []);
        setFolders(Array.isArray(data?.folders) ? data.folders : []);
        setFiles(Array.isArray(data?.files) ? data.files : []);
      }
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить данные');
      setCurrentFolder(null);
      setBreadcrumbs([]);
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId, isRoot]);

  const loadSearchResults = useCallback(async () => {
    if (!trimmedSearchQuery) {
      setSearchResults([]);
      return;
    }

    setSearching(true);
    setError('');

    try {
      const params = new URLSearchParams();
      params.set('q', trimmedSearchQuery);

      if (!isRoot && currentFolderId) {
        params.set('folder_id', String(currentFolderId));
      }

      const res = await apiFetch(`${API_URL}/dexter/files/search?${params.toString()}`);
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось выполнить поиск'));
      }

      const data = await res.json();
      setSearchResults(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setError(e?.message || 'Не удалось выполнить поиск');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [trimmedSearchQuery, isRoot, currentFolderId]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  useEffect(() => {
    if (!trimmedSearchQuery) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    loadSearchResults();
  }, [trimmedSearchQuery, loadSearchResults]);

  useEffect(() => {
    if (!isCreateFolderFormOpen) return;
    document.getElementById('dexter-folder-name-input')?.focus();
  }, [isCreateFolderFormOpen]);

  useEffect(() => {
    if (isRoot) setIsUploadFormOpen(false);
  }, [isRoot]);

  useEffect(() => {
    if (!renameFolderId) return;

    const onMouseDown = (e) => {
      const el = e.target;
      if (el && typeof el.closest === 'function') {
        const isInsideRename = el.closest('[data-rename-wrapper="true"]');
        if (isInsideRename) return;
      }
      setRenameFolderId(null);
      setRenameFolderName('');
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [renameFolderId]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!openFolderMenuId) return;
      if (!e.target.closest?.(`.${s.folderMenuWrap}`)) {
        setOpenFolderMenuId(null);
      }
    };

    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openFolderMenuId]);

  const pageTitle = useMemo(() => {
    if (isRoot) return 'Файлы';
    return currentFolder?.name || 'Файлы';
  }, [currentFolder, isRoot]);
  const folderAndFileCountLabel = useMemo(() => {
    const foldersCount = folders.length;
    const filesCount = files.length;
    if (foldersCount === 0 && filesCount === 0) return '';
    if (foldersCount === 0) {
      return `${filesCount} ${pluralize(filesCount, ['файл', 'файла', 'файлов'])}`;
    }
    if (filesCount === 0) {
      return `${foldersCount} ${pluralize(foldersCount, ['папка', 'папки', 'папок'])}`;
    }
    return `${foldersCount} ${pluralize(foldersCount, ['папка', 'папки', 'папок'])} · ${filesCount} ${pluralize(filesCount, ['файл', 'файла', 'файлов'])}`;
  }, [folders, files]);

  async function handleDownload(item) {
    if (!item?.id) return;

    const iosWindow = isIOS ? window.open('', '_blank') : null;

    try {
      setBusyKey(`file-download-${item.id}`);

      const res = await apiFetch(`${API_URL}/dexter/files/${item.id}/download`);
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось получить ссылку на скачивание'));
      }

      const data = await res.json();
      if (!data?.url) throw new Error('Пустая ссылка на скачивание');

      if (iosWindow && !iosWindow.closed) {
        iosWindow.location.replace(data.url);
        return;
      }

      openFileUrl(data.url, item.original_filename || '', isIOS);
    } catch (e) {
      if (iosWindow && !iosWindow.closed) iosWindow.close();
      alert(e?.message || 'Ошибка скачивания файла');
    } finally {
      setBusyKey('');
    }
  }

  async function handleDeleteFile(item) {
    if (!item?.id) return;

    const ok = window.confirm(`Удалить файл "${item.original_filename}"?`);
    if (!ok) return;

    try {
      setBusyKey(`file-delete-${item.id}`);

      const res = await apiFetch(`${API_URL}/dexter/files/${item.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось удалить файл'));
      }

      setFiles((prev) => prev.filter((x) => x.id !== item.id));
      setSearchResults((prev) => prev.filter((x) => x.id !== item.id));
    } catch (e) {
      alert(e?.message || 'Ошибка удаления файла');
    } finally {
      setBusyKey('');
    }
  }

  async function handleShareFile(item) {
    if (!item?.id) return;
    console.log('share support:', !!navigator.share);
    try {
      if (!navigator.share) {
        alert('На этом устройстве не поддерживается отправка файла через меню "Поделиться". Открою/скачаю файл вместо этого.');
        await handleDownload(item);
        return;
      }

      if (typeof window !== 'undefined' && !window.isSecureContext) {
        alert('Для "Поделиться" нужен HTTPS (secure context). Открою/скачаю файл вместо этого.');
        await handleDownload(item);
        return;
      }

      setBusyKey(`file-share-${item.id}`);
      const res = await apiFetch(`${API_URL}/dexter/files/${item.id}/download`);
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось получить файл для отправки'));
      }

      const data = await res.json();
      if (!data?.url) throw new Error('Пустая ссылка на файл');

      const fileRes = await fetch(data.url);
      if (!fileRes.ok) throw new Error('Не удалось получить файл для отправки');
      const blob = await fileRes.blob();

      const name = item.original_filename || `file-${item.id}`;
      const file = new File([blob], name, { type: blob.type || 'application/octet-stream' });
      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        alert('Это устройство/браузер не поддерживает отправку файлов из браузера. Открою/скачаю файл вместо этого.');
        await handleDownload(item);
        return;
      }

      try {
        await navigator.share({ title: name, files: [file] });
      } catch (e) {
        const name = e?.name || '';
        const msg = String(e?.message || '').toLowerCase();
        const isCancel =
          name === 'AbortError' ||
          msg.includes('abort') ||
          msg.includes('cancel');

        if (!isCancel) {
          alert('Не удалось открыть меню "Поделиться".');
        }
      }
    } catch (e) {
      alert(e?.message || 'Не удалось подготовить файл для отправки');
    } finally {
      setBusyKey('');
    }
  }

  async function handleCreateFolder(e) {
    e.preventDefault();
    if (!canManageDexter) return;

    const name = folderName.trim();
    if (!name) {
      alert('Введите название папки');
      return;
    }

    try {
      setBusyKey('create-folder');

      const res = await apiFetch(`${API_URL}/dexter/folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          parent_id: currentFolderId,
        }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось создать папку'));
      }

      const created = await res.json();
      setFolders((prev) => [...prev, created].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru')));
      setFolderName('');
    } catch (e) {
      alert(e?.message || 'Ошибка создания папки');
    } finally {
      setBusyKey('');
    }
  }

  function startRenameFolder(folder) {
    setOpenFolderMenuId(null);
    setRenameFolderId(folder.id);
    setRenameFolderName(folder.name || '');
  }

  function cancelRenameFolder() {
    setRenameFolderId(null);
    setRenameFolderName('');
  }

  async function handleRenameFolder(folder) {
    const name = renameFolderName.trim();
    if (!name) {
      alert('Введите новое название папки');
      return;
    }

    try {
      setBusyKey(`folder-rename-${folder.id}`);

      const res = await apiFetch(`${API_URL}/dexter/folders/${folder.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось переименовать папку'));
      }

      const updated = await res.json();

      setFolders((prev) =>
        prev
          .map((x) => (x.id === updated.id ? updated : x))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'))
      );

      setBreadcrumbs((prev) =>
        prev.map((x) => (x.id === updated.id ? { ...x, name: updated.name } : x))
      );

      if (currentFolder?.id === updated.id) {
        setCurrentFolder(updated);
      }

      cancelRenameFolder();
    } catch (e) {
      alert(e?.message || 'Ошибка переименования папки');
    } finally {
      setBusyKey('');
    }
  }

  async function handleDeleteFolder(folder) {
    if (!folder?.id) return;
    setOpenFolderMenuId(null);

    const ok = window.confirm(
      `Удалить папку "${folder.name}" вместе со всеми подпапками и файлами?`
    );
    if (!ok) return;

    try {
      setBusyKey(`folder-delete-${folder.id}`);

      const res = await apiFetch(`${API_URL}/dexter/folders/${folder.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(extractErrorText(txt, 'Не удалось удалить папку'));
      }

      setFolders((prev) => prev.filter((x) => x.id !== folder.id));
    } catch (e) {
      alert(e?.message || 'Ошибка удаления папки');
    } finally {
      setBusyKey('');
    }
  }

  async function handleUpload(e) {
    e.preventDefault();
    if (!canManageDexter) return;

    if (isRoot) {
      alert('Сначала откройте папку, в которую нужно загрузить файл');
      return;
    }

    if (!filesToUpload.length) {
      alert('Выберите хотя бы один файл');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const createdItems = [];

      for (const file of filesToUpload) {
        const mime = file.type || 'application/octet-stream';

        const presignRes = await apiFetch(`${API_URL}/dexter/files/presign-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            filename: file.name,
            mime,
            folder_id: currentFolderId,
          }),
        });

        if (!presignRes.ok) {
          const txt = await presignRes.text().catch(() => '');
          throw new Error(extractErrorText(txt, `Не удалось получить ссылку на загрузку: ${file.name}`));
        }

        const presignData = await presignRes.json();

        const uploadRes = await fetch(presignData.url, {
          method: presignData.method || 'PUT',
          headers: presignData.headers || { 'Content-Type': mime },
          body: file,
        });

        if (!uploadRes.ok) {
          throw new Error(`Не удалось загрузить файл в хранилище: ${file.name}`);
        }

        const createRes = await apiFetch(`${API_URL}/dexter/files`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            original_filename: file.name,
            s3_key: presignData.s3_key,
            mime,
            size: file.size,
            folder_id: currentFolderId,
          }),
        });

        if (!createRes.ok) {
          const txt = await createRes.text().catch(() => '');
          throw new Error(extractErrorText(txt, `Не удалось сохранить файл: ${file.name}`));
        }

        const created = await createRes.json();
        createdItems.push(created);
      }

      setFiles((prev) => [...createdItems, ...prev]);
      setFilesToUpload([]);

      const input = document.getElementById('dexter-file-input');
      if (input) input.value = '';
    } catch (e) {
      alert(e?.message || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  }

  const displayedFiles = isSearchMode ? searchResults : files;

  return (
    <div className={s.page}>
      <div className={s.wrap}>
        <div className={s.header}>
          <div className={s.headerText}>
            <div className={s.titleRow}>
              <h1 className={s.title}>
                {pageTitle}
              </h1>
              {folderAndFileCountLabel ? (
                <span className={s.countBadge}>{folderAndFileCountLabel}</span>
              ) : null}
            </div>
            <p className={s.subtitle}>
              {isRoot
                ? 'Папки и файлы компании для скачивания и быстрого доступа к информации.'
                : 'Откройте папку, скачивайте файлы и управляйте файловой структурой.'}
            </p>
          </div>
          <button
            type="button"
            className={s.refreshBtn}
            onClick={() => {
              loadContent();
              if (trimmedSearchQuery) {
                loadSearchResults();
              }
            }}
            disabled={loading || uploading || searching}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M2 10C2 10 4.00498 7.26822 5.63384 5.63824C7.26269 4.00827 9.5136 3 12 3C16.9706 3 21 7.02944 21 12C21 16.9706 16.9706 21 12 21C7.89691 21 4.43511 18.2543 3.35177 14.5M2 10V4M2 10H8"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
        </div>

        {!isRoot && breadcrumbs.length > 0 && (
          <div className={s.filtersRow}>
            <nav className={s.breadcrumbs} aria-label="Навигация по папкам">
              <button type="button" className={s.breadcrumbLink} onClick={openRoot}>
                Файлы
              </button>

              {breadcrumbs.map((crumb, index) => {
                const isLast = index === breadcrumbs.length - 1;
                return (
                  <React.Fragment key={crumb.id}>
                    <span className={s.breadcrumbIcon} aria-hidden="true">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {isLast ? (
                      <span className={s.breadcrumbCurrent}>{crumb.name}</span>
                    ) : (
                      <button
                        type="button"
                        className={s.breadcrumbLink}
                        onClick={() => openFolder(crumb.id)}
                      >
                        {crumb.name}
                      </button>
                    )}
                  </React.Fragment>
                );
              })}
            </nav>
          </div>
        )}

        {canManageDexter && (
          <>
            {isCreateFolderFormOpen && (
              <div className={s.card}>
                <form onSubmit={handleCreateFolder} className={s.formGrid}>
                  <div className={s.panelTitle}>
                    {isRoot ? 'Создать корневую папку' : 'Создать подпапку'}
                  </div>

                  <Input
                    id="dexter-folder-name-input"
                    type="text"
                    placeholder="Название папки"
                    ariaLabel="Название папки"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                  />

                  <div className={s.buttonRow}>
                    <button
                      type="submit"
                      className={s.primaryBtn}
                      disabled={busyKey === 'create-folder'}
                    >
                      Создать папку
                    </button>
                  </div>
                </form>
              </div>
            )}

            {!isRoot && isUploadFormOpen && (
              <div className={s.card}>
                <form onSubmit={handleUpload} className={s.formGrid}>
                  <div className={s.panelTitle}>Загрузка файлов</div>

                  <div
                    className={`${s.dropzone} ${isDragActive ? s.dropzoneActive : ''}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setIsDragActive(true);
                    }}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setIsDragActive(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setIsDragActive(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragActive(false);
                      mergeFiles(e.dataTransfer.files);
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 8L12 3M12 3L7 8M12 3V15"
                        stroke="#BD23DB"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <div className={s.dropzoneTitle}>Перетащите файлы сюда</div>
                    <div className={s.dropzoneText}>или выберите их ниже через системный диалог</div>
                  </div>

                  <input
                    id="dexter-file-input"
                    className={s.input}
                    type="file"
                    multiple
                    onChange={(e) => {
                      mergeFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />

                  {filesToUpload.length > 0 && (
                    <div className={s.uploadList}>
                      {filesToUpload.map((file) => {
                        const key = getUploadFileKey(file);
                        return (
                          <div key={key} className={s.uploadItem}>
                            <span className={s.uploadItemName} title={file.name}>
                              {file.name}
                            </span>
                            <button
                              type="button"
                              className={s.uploadRemoveBtn}
                              onClick={() => {
                                if (!key) return;
                                setFilesToUpload((prev) =>
                                  prev.filter((f) => getUploadFileKey(f) !== key)
                                );
                              }}
                              aria-label={`Убрать файл: ${file.name}`}
                              disabled={uploading}
                            >
                              ×
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className={s.buttonRow}>
                    <button
                      type="submit"
                      className={s.primaryBtn}
                      disabled={uploading}
                    >
                      {uploading ? 'Загрузка...' : 'Загрузить файлы'}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </>
        )}

        <div className={s.card}>
          <div className={s.searchBarRow}>
            <SearchInput
              className={s.searchInputControl}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isRoot ? 'Поиск файлов...' : 'Поиск в текущей папке...'}
              ariaLabel={isRoot ? 'Поиск в разделе «Файлы»' : 'Поиск файлов в текущей папке'}
            />

            <div className={s.searchActions}>
              {/* {isSearchMode && (
                <button
                  type="button"
                  className={s.secondaryBtn}
                  onClick={() => setSearchQuery('')}
                  disabled={searching}
                >
                  Очистить
                </button>
              )} */}

              {canManageDexter && !isRoot && (
                <button
                  type="button"
                  className={s.primaryBtn}
                  onClick={() => setIsUploadFormOpen((prev) => !prev)}
                  disabled={uploading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M14 2.26953V6.40007C14 6.96012 14 7.24015 14.109 7.45406C14.2049 7.64222 14.3578 7.7952 14.546 7.89108C14.7599 8.00007 15.0399 8.00007 15.6 8.00007H19.7305M12 18V12M9 15H15M14 2H8.8C7.11984 2 6.27976 2 5.63803 2.32698C5.07354 2.6146 4.6146 3.07354 4.32698 3.63803C4 4.27976 4 5.11984 4 6.8V17.2C4 18.8802 4 19.7202 4.32698 20.362C4.6146 20.9265 5.07354 21.3854 5.63803 21.673C6.27976 22 7.11984 22 8.8 22H15.2C16.8802 22 17.7202 22 18.362 21.673C18.9265 21.3854 19.3854 20.9265 19.673 20.362C20 19.7202 20 18.8802 20 17.2V8L14 2Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {isUploadFormOpen ? 'Скрыть загрузку' : 'Загрузить'}
                </button>
              )}

              {canManageDexter && (
                <button
                  type="button"
                  className={s.secondaryBtn}
                  onClick={() => setIsCreateFolderFormOpen((prev) => !prev)}
                  disabled={loading || uploading}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M13 7L11.8845 4.76892C11.5634 4.1268 11.4029 3.80573 11.1634 3.57116C10.9516 3.36373 10.6963 3.20597 10.4161 3.10931C10.0992 3 9.74021 3 9.02229 3H5.2C4.0799 3 3.51984 3 3.09202 3.21799C2.71569 3.40973 2.40973 3.71569 2.21799 4.09202C2 4.51984 2 5.0799 2 6.2V7M2 7H17.2C18.8802 7 19.7202 7 20.362 7.32698C20.9265 7.6146 21.3854 8.07354 21.673 8.63803C22 9.27976 22 10.1198 22 11.8V16.2C22 17.8802 22 18.7202 21.673 19.362C21.3854 19.9265 20.9265 20.3854 20.362 20.673C19.7202 21 18.8802 21 17.2 21H6.8C5.11984 21 4.27976 21 3.63803 20.673C3.07354 20.3854 2.6146 19.9265 2.32698 19.362C2 18.7202 2 17.8802 2 16.2V7ZM12 17V11M9 14H15"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  {isCreateFolderFormOpen ? 'Скрыть форму' : 'Создать папку'}
                </button>
              )}
            </div>
          </div>
        </div>

        {error ? (
          <div className={`${s.card} ${s.errorCard}`}>{error}</div>
        ) : null}

        {loading ? (
          <div className={s.emptyState}>Загрузка данных...</div>
        ) : (
          <>
            {!isSearchMode && folders.length > 0 && (
              <>
                <div className={s.sectionTitle}>Папки</div>
                <div className={`${s.list} ${s.folderGrid}`}>
                  {folders.map((folder) => (
                    <div key={folder.id} className={`${s.fileRow} ${s.folderCard}`}>
                      <div
                        className={s.fileRowMain}
                        role="button"
                        tabIndex={0}
                        onClick={() => openFolder(folder.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            openFolder(folder.id);
                          }
                        }}
                      >
                        <div className={s.fileIconWrap}>
                          <div className={s.fileIcon}>📁</div>
                        </div>

                        <div className={s.fileMeta}>
                          {renameFolderId === folder.id ? (
                            <div className={s.formGrid} data-rename-wrapper="true">
                              <Input
                                type="text"
                                value={renameFolderName}
                                onChange={(e) => setRenameFolderName(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                                ariaLabel="Новое название папки"
                                className={s.renameFolderInput}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRenameFolder(folder);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    cancelRenameFolder();
                                  } else if (e.key === ' ') {
                                    e.stopPropagation();
                                  }
                                }}
                              />
                            </div>
                          ) : (
                            <>
                              <p className={s.fileTitle}>{folder.name}</p>
                              <div className={s.fileSub}>
                                <div className={s.fileInfo}>
                                  {(folder.updated_at || folder.created_at)
                                    ? `изм. ${formatDate(folder.updated_at || folder.created_at)}`
                                    : ''}
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      {renameFolderId !== folder.id && canManageDexter && (
                        <div className={s.folderMenuWrap}>
                          <button
                            type="button"
                            className={s.folderMenuBtn}
                            aria-label="Действия с папкой"
                            aria-haspopup="menu"
                            aria-expanded={openFolderMenuId === folder.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFolderMenuId((prev) => (prev === folder.id ? null : folder.id));
                            }}
                            disabled={busyKey === `folder-delete-${folder.id}`}
                          >
                            <ActionsMenuDotsIcon />
                          </button>

                          <DropdownMenu
                            open={openFolderMenuId === folder.id}
                            align="left"
                            items={[
                              {
                                key: 'rename',
                                label: 'Переименовать',
                                disabled: busyKey === `folder-delete-${folder.id}`,
                                onClick: () => startRenameFolder(folder),
                              },
                              {
                                key: 'delete',
                                label: 'Удалить',
                                danger: true,
                                disabled: busyKey === `folder-delete-${folder.id}`,
                                onClick: () => handleDeleteFolder(folder),
                              },
                            ]}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}

            {isSearchMode && (
              <div className={s.card}>
                <div className={s.fileSub}>
                  {searching
                    ? 'Поиск...'
                    : `Найдено файлов: ${searchResults.length}`}
                </div>
              </div>
            )}

            {(isSearchMode ? searchResults.length > 0 : files.length > 0) && (
              <>
                <div className={s.sectionTitle}>{isSearchMode ? 'Результаты поиска' : 'Файлы'}</div>
                <div className={`${s.card} ${s.filesTableCard}`}>
                  <div
                    className={`${s.filesTableHead} ${
                      !showActionsColumn ? s.filesTableHeadNoActions : ''
                    }`}
                  >
                    <div>Название</div>
                    <div className={s.filesTableHeadSize}>Размер</div>
                    <div className={s.filesTableHeadDate}>Дата</div>
                    {showActionsColumn ? <div /> : null}
                  </div>

                  <div className={s.list}>
                    {displayedFiles.map((item) => {
                      const fileBadge = getFileBadgeType(item.original_filename);
                      return (
                        <div
                          key={item.id}
                          className={`${s.fileRow} ${s.fileTableRow} ${
                            !showActionsColumn ? s.fileTableRowNoActions : ''
                          }`}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleDownload(item)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleDownload(item);
                            }
                          }}
                          aria-label={`Скачать ${item.original_filename}`}
                        >
                          <div className={s.fileRowMain}>
                            <div className={s.fileIconWrap}>
                              <div className={`${s.fileTypeBadge} ${s[fileBadge.className]}`}>
                                {fileBadge.label}
                              </div>
                            </div>

                            <div className={s.fileMeta}>
                              <p className={s.fileTitle}>{item.original_filename}</p>
                              <div className={s.fileMetaCompact}>
                                <span>{formatBytes(item.size)}</span>  ·
                                <span>{item.created_at ? formatDate(item.created_at) : '—'}</span>
                              </div>
                              {isSearchMode && item.folder_path ? (
                                <div className={s.fileSub}>
                                  <div className={s.fileInfo}>{item.folder_path}</div>
                                </div>
                              ) : null}
                            </div>
                          </div>

                          <div className={s.fileSizeCell}>{formatBytes(item.size)}</div>
                          <div className={s.fileDateCell}>
                            {item.created_at ? formatDate(item.created_at) : '—'}
                          </div>
                          {showActionsColumn ? (
                            <div className={`${s.buttonRow} ${s.tableActions}`}>
                            {isIOS && (
                              <button
                                type="button"
                                className={s.iconBtnSecondary}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void handleShareFile(item);
                                }}
                                disabled={busyKey === `file-share-${item.id}`}
                                aria-label="Поделиться файлом"
                                title="Поделиться"
                              >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <path
                                    d="M7 11C6.07003 11 5.60504 11 5.22354 11.1022C4.18827 11.3796 3.37962 12.1883 3.10222 13.2235C3 13.605 3 14.07 3 15V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15C21 14.07 21 13.605 20.8978 13.2235C20.6204 12.1883 19.8117 11.3796 18.7765 11.1022C18.395 11 17.93 11 17 11M16 7L12 3M12 3L8 7M12 3V15"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>

                              </button>
                            )}

                            {isSearchMode && item.folder_id && (
                              <button
                                type="button"
                                className={s.iconBtnSecondary}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openFolder(item.folder_id);
                                }}
                                aria-label="Открыть папку"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M4 7.2C4 6.0799 4 5.51984 4.21799 5.09202C4.40973 4.71569 4.71569 4.40973 5.09202 4.21799C5.51984 4 6.0799 4 7.2 4H10.2C10.6574 4 10.8861 4 11.1005 4.05526C11.3149 4.11052 11.4969 4.2183 11.8609 4.43386L13.25 5.25C13.614 5.46556 13.7961 5.57334 14.0105 5.6286C14.2249 5.68386 14.4536 5.68386 14.9111 5.68386H16.8C17.9201 5.68386 18.4802 5.68386 18.908 5.90184C19.2843 6.09358 19.5903 6.39954 19.782 6.77587C20 7.2037 20 7.76376 20 8.88386V16.8C20 17.9201 20 18.4802 19.782 18.908C19.5903 19.2843 19.2843 19.5903 18.908 19.782C18.4802 20 17.9201 20 16.8 20H7.2C6.0799 20 5.51984 20 5.09202 19.782C4.71569 19.5903 4.40973 19.2843 4.21799 18.908C4 18.4802 4 17.9201 4 16.8V7.2Z"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinejoin="round"
                                  />
                                  <path
                                    d="M4 9H20"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                  />
                                </svg>
                              </button>
                            )}

                            {canManageDexter && (
                              <button
                                type="button"
                                className={s.iconBtnSecondary}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownload(item);
                                }}
                                disabled={busyKey === `file-download-${item.id}`}
                                aria-label="Скачать"
                                title="Скачать"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M21 15V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V15M17 10L12 15M12 15L7 10M12 15V3"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            )}

                            {canManageDexter && (
                              <button
                                type="button"
                                className={s.iconBtnDanger}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteFile(item);
                                }}
                                disabled={busyKey === `file-delete-${item.id}`}
                                aria-label="Удалить"
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  aria-hidden="true"
                                >
                                  <path
                                    d="M16 6V5.2C16 4.0799 16 3.51984 15.782 3.09202C15.5903 2.71569 15.2843 2.40973 14.908 2.21799C14.4802 2 13.9201 2 12.8 2H11.2C10.0799 2 9.51984 2 9.09202 2.21799C8.71569 2.40973 8.40973 2.71569 8.21799 3.09202C8 3.51984 8 4.0799 8 5.2V6M10 11.5V16.5M14 11.5V16.5M3 6H21M19 6V17.2C19 18.8802 19 19.7202 18.673 20.362C18.3854 20.9265 17.9265 21.3854 17.362 21.673C16.7202 22 15.8802 22 14.2 22H9.8C8.11984 22 7.27976 22 6.63803 21.673C6.07354 21.3854 5.6146 20.9265 5.32698 20.362C5 19.7202 5 18.8802 5 17.2V6"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </button>
                            )}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {!isSearchMode && !folders.length && !files.length && (
              <div className={s.emptyState}>
                {isRoot
                  ? 'В разделе «Файлы» пока нет папок'
                  : 'В этой папке пока нет подпапок и файлов'}
              </div>
            )}

            {isSearchMode && !searching && !searchResults.length && (
              <div className={s.emptyState}>
                Ничего не найдено
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}